import {
  dashboardRouteSearchSchema,
  type DashboardRouteSearch,
} from '../../dashboard/dashboardRouteSearch';
import { defaultBrandsFilter, type BrandsFilter } from './model';
import { optionalPositiveInteger, optionalTrimmedString } from '../../../lib/routeSearch';

export type BrandsRouteSearch = DashboardRouteSearch & {
  agencies?: string;
  search?: string;
  industries?: string;
  page?: number;
  size?: number;
};

export type BrandsRouteFilters = Pick<BrandsFilter, 'name' | 'industries' | 'page' | 'size'> & {
  agencyIds?: string[];
};

export function brandsRouteSearchSchema(search: Record<string, unknown>): BrandsRouteSearch {
  const agencies = optionalTrimmedString(search['agencies']);
  const page = optionalPositiveInteger(search['page']);
  const size = optionalPositiveInteger(search['size']);
  const name = optionalTrimmedString(search['search']);
  const industries = optionalTrimmedString(search['industries']);

  return {
    ...dashboardRouteSearchSchema(search),
    ...(agencies ? { agencies } : {}),
    ...(name ? { search: name } : {}),
    ...(industries ? { industries } : {}),
    ...(page ? { page } : {}),
    ...(size ? { size } : {}),
  };
}

export function parseBrandsRouteSearch(search: BrandsRouteSearch): BrandsRouteFilters {
  return {
    ...(search.agencies
      ? {
          agencyIds: search.agencies
            .split(',')
            .map((id) => id.trim())
            .filter(Boolean),
        }
      : {}),
    name: search.search ?? defaultBrandsFilter.name,
    industries: splitValues(search.industries),
    page: search.page ? search.page - 1 : defaultBrandsFilter.page,
    size: search.size ?? defaultBrandsFilter.size,
  };
}

export function serializeBrandsRouteSearch(filters: BrandsRouteFilters): BrandsRouteSearch {
  return {
    ...(filters.agencyIds?.length ? { agencies: filters.agencyIds.join(',') } : {}),
    ...(filters.name.trim() ? { search: filters.name.trim() } : {}),
    ...(filters.industries.length ? { industries: JSON.stringify(filters.industries) } : {}),
    ...(filters.page ? { page: filters.page + 1 } : {}),
    ...(filters.size !== defaultBrandsFilter.size ? { size: filters.size } : {}),
  };
}

function splitValues(value: string | undefined) {
  if (!value) {
    return [];
  }

  const parsedValue = parseIndustryValues(value);
  if (parsedValue) {
    return parsedValue;
  }

  return value
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);
}

function parseIndustryValues(value: string) {
  try {
    const parsed: unknown = JSON.parse(value);
    return Array.isArray(parsed) && parsed.every((item) => typeof item === 'string')
      ? parsed.filter(Boolean)
      : null;
  } catch {
    return null;
  }
}
